import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { adminLogin, adminMe } from "../lib/adminApi";

/**
 * AdminLogin — Plena ekibinin sade admin paneli girişi.
 * Hiç marketing yok. Pazarlama amaçlı sayfa müşteri demosu (/demo/:slug) içinde.
 */
export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@plenagrowth.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If already logged in, skip to /admin
  useEffect(() => {
    const t = sessionStorage.getItem("plena_admin_token");
    if (t) {
      adminMe().then(() => nav("/admin", { replace: true })).catch(() => sessionStorage.removeItem("plena_admin_token"));
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await adminLogin(email, password);
      sessionStorage.setItem("plena_admin_token", data.token);
      sessionStorage.setItem("plena_admin_email", data.email);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center shadow-md">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight text-left">
            <div className="font-display text-lg font-bold tracking-tight">Plena Growth</div>
            <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">Admin Console</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-7">
          <div className="mb-6">
            <h2 className="font-display text-xl font-semibold text-slate-900">Yönetici Girişi</h2>
            <p className="text-xs text-slate-500 mt-1">
              Müşteri demolarınızı yönetmek için oturum açın.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold text-slate-700">E-posta</Label>
              <Input
                id="email"
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                placeholder="admin@plenagrowth.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-slate-700">Şifre</Label>
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
                placeholder="••••••••••••"
                required
                autoFocus
              />
            </div>
            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="admin-login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {loading ? "Giriş yapılıyor…" : "Giriş yap"}
            </Button>
          </form>
        </div>

        <p className="text-center text-[0.65rem] text-slate-400 mt-6">
          © 2026 Plena Labs · v1.0
        </p>
      </div>
    </div>
  );
}
